import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

function Quotes() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await api.getJobs();
      // Only include jobs that have quotes (quote_line_items)
      const jobsWithQuotes = data.filter(job => job.quote_line_items?.length > 0);
      setJobs(jobsWithQuotes);
    } catch (err) {
      setError(err.message || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  // Get quote status for a job
  const getQuoteStatus = (job) => {
    if (job.quote_accepted_at) return 'accepted';
    if (job.quote_sent_at) return 'sent';
    return 'draft';
  };

  // Calculate quote total
  const getQuoteTotal = (job) => {
    // Use pre-calculated total if available
    if (job.quote_total_with_tax) return parseFloat(job.quote_total_with_tax);
    if (job.quote_total) return parseFloat(job.quote_total);

    if (!job.quote_line_items?.length) return 0;
    return job.quote_line_items.reduce((sum, item) => {
      const isLabor = item.type === 'labor' || item.type === 'hourly' || item.type === 'daily_labor';
      if (isLabor) {
        return sum + (parseFloat(item.hours || 0) * parseFloat(item.rate || 0));
      }
      return sum + parseFloat(item.cost || 0);
    }, 0);
  };

  // Format currency
  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  // Filter jobs based on status and search
  const filteredJobs = jobs.filter((job) => {
    const status = getQuoteStatus(job);
    const matchesFilter = filter === 'all' || status === filter;
    const matchesSearch = !searchQuery ||
      job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  // Get counts for filter badges
  const getCounts = () => {
    return {
      all: jobs.length,
      draft: jobs.filter(j => getQuoteStatus(j) === 'draft').length,
      sent: jobs.filter(j => getQuoteStatus(j) === 'sent').length,
      accepted: jobs.filter(j => getQuoteStatus(j) === 'accepted').length,
    };
  };

  const counts = getCounts();

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading quotes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Quotes</h1>
            <p className="text-slate-600">Manage quotes for your jobs</p>
          </div>
          <Link
            to="/jobs"
            className="inline-flex items-center space-x-2 bg-white border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-xl font-bold hover:border-primary hover:text-primary transition"
          >
            <i className="fas fa-briefcase"></i>
            <span>View Jobs</span>
          </Link>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Search quotes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'draft', label: 'Draft' },
            { key: 'sent', label: 'Sent' },
            { key: 'accepted', label: 'Accepted' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                filter === key
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-primary'
              }`}
            >
              {label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                filter === key ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Quotes List */}
        {filteredJobs.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center">
            <i className="fas fa-file-invoice text-6xl text-slate-300 mb-4"></i>
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Quotes Found</h3>
            <p className="text-slate-600 mb-6">
              {jobs.length === 0
                ? 'Create quotes from job details to get started.'
                : 'No quotes match your current filters.'}
            </p>
            {jobs.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilter('all');
                }}
                className="text-secondary font-medium hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredJobs.map((job) => {
              const status = getQuoteStatus(job);
              const total = getQuoteTotal(job);
              return (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition block"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-primary">{job.title}</h3>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(status)}`}>
                          {status}
                        </span>
                      </div>
                      {job.client_name && (
                        <p className="text-slate-600 mb-1">
                          <i className="fas fa-user mr-2 text-slate-400"></i>
                          {job.client_name}
                        </p>
                      )}
                      <p className="text-sm text-slate-500">
                        <i className="fas fa-list mr-2 text-slate-400"></i>
                        {job.quote_line_items.length} line item{job.quote_line_items.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Total</p>
                        <p className="text-xl font-bold text-primary">
                          {formatCurrency(total)}
                        </p>
                      </div>
                      {job.quote_sent_at && (
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Sent</p>
                          <p className="text-sm font-medium text-slate-700">
                            {new Date(job.quote_sent_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      <i className="fas fa-chevron-right text-slate-400"></i>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Summary */}
        {filteredJobs.length > 0 && (
          <div className="mt-6 text-center text-slate-600">
            Showing {filteredJobs.length} of {jobs.length} quotes
          </div>
        )}
      </div>
    </div>
  );
}

export default Quotes;
